import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tag, X } from "lucide-react";

/**
 * Discreet promo-code input. `applied` is the currently applied code (or null).
 * `onApply(code)` must return a promise that resolves on success / rejects on invalid code.
 * `onRemove()` clears the code.
 */
export const PromoField = ({ applied, onApply, onRemove, disabled }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  if (applied) {
    return (
      <div className="flex items-center justify-between text-xs text-[#3F5036]" data-testid="promo-applied">
        <span className="inline-flex items-center gap-1.5"><Tag className="h-3 w-3" /> {applied}</span>
        {!disabled && (
          <button type="button" onClick={onRemove} data-testid="promo-remove-btn"
                  className="inline-flex items-center gap-1 text-[#8C7A6B] hover:text-[#C86D53]">
            <X className="h-3 w-3" /> {t("detail.promo_remove")}
          </button>
        )}
      </div>
    );
  }

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="promo-toggle-btn"
              className="text-xs text-[#8C7A6B] hover:text-[#C86D53] underline-offset-2 hover:underline">
        {t("detail.promo_label")}
      </button>
    );
  }

  const submit = async () => {
    const code = value.trim();
    if (!code) return;
    setBusy(true); setError(false);
    try {
      await onApply(code);
      setValue(""); setOpen(false);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="promo-form">
      <div className="flex items-center gap-2">
        <input
          data-testid="promo-input"
          value={value}
          onChange={(e) => { setValue(e.target.value.toUpperCase()); setError(false); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
          placeholder={t("detail.promo_label")}
          className={`flex-1 text-xs border rounded-lg px-3 py-2 outline-none bg-transparent uppercase tracking-wider ${error ? "border-[#C86D53]" : "border-[#EAE3D9] focus:border-[#C86D53]"}`}
          autoFocus
        />
        <button type="button" onClick={submit} disabled={busy || !value.trim()} data-testid="promo-apply-btn"
                className="text-xs px-3 py-2 rounded-lg border border-[#2C2A29] text-[#2C2A29] hover:bg-[#2C2A29] hover:text-white transition-colors disabled:opacity-40">
          {busy ? "…" : t("detail.promo_apply")}
        </button>
      </div>
      {error && <p className="text-[10px] text-[#C86D53] mt-1" data-testid="promo-error">{t("detail.promo_invalid")}</p>}
    </div>
  );
};
